import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { API, Unit } from '@getgreenline/homi-shared'
import { Alert, Select, InputNumber, message, Popconfirm } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { BLAZE_COMPANY } from '../../../../../constants/general'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  onSave: () => void
}

interface State {
  isSaving: boolean
  locationId?: number
  categoryId?: string
  unit?: Unit
  lowStockQuantity?: number
  restockQuantity?: number
  affectedEntries?: number // Will be set after saving
  isConfirmationVisible: boolean
}

@inject('currentCompanyStore')
@observer
export class Update extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isSaving: false,
      isConfirmationVisible: false,
    }
  }

  componentDidMount() {
    this.props.currentCompanyStore?.productStore?.getCategories()
  }

  handleOnSave = () => {
    if (
      this.state.lowStockQuantity === undefined ||
      this.state.lowStockQuantity === 0 ||
      this.state.restockQuantity === undefined ||
      this.state.restockQuantity === 0
    ) {
      this.setState({ isConfirmationVisible: true })
    } else {
      this.save()
    }
  }

  save = async () => {
    const company = this.props.currentCompanyStore?.company

    if (!company) {
      return
    }

    const { locationId, categoryId, lowStockQuantity, restockQuantity, unit } = this.state

    if (locationId === undefined) {
      return message.error('Please select a location.')
    }

    try {
      this.setState({ isSaving: true })

      const response = await API.bulkUpdateStockAlertLevels(company.id, locationId, {
        lowStockQuantity,
        restockQuantity,
        categoryId,
        unit,
      })

      this.props.onSave()

      this.setState({ affectedEntries: response.results })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while updating stock alert levels')
    } finally {
      this.setState({ isSaving: false })
    }
  }

  get unitOptions() {
    return [
      { label: 'units', value: 'unit' },
      { label: 'grams', value: 'gram' },
    ]
  }

  get saveButton(): JSX.Element {
    const title =
      this.state.locationId === undefined
        ? 'Location required'
        : this.state.isSaving
        ? 'Saving...'
        : 'Save'

    const isDisabled = this.state.locationId === undefined || this.state.isSaving

    return (
      <Popconfirm
        title='Low stock or restock value was not provided. All values will be removed. Are you sure you want to proceed?'
        visible={this.state.isConfirmationVisible}
        onConfirm={this.save}
        onCancel={() => this.setState({ isConfirmationVisible: false })}
        onVisibleChange={this.handleOnSave}
        okText='Confirm'
        cancelText='Cancel'
      >
        <BlazeButton
          buttonProps={{
            type: 'primary',
            className: 'w-100',
            disabled: isDisabled,
            onClick: () => this.handleOnSave(),
          }}
          titleProps={{
            title,
          }}
        />
      </Popconfirm>
    )
  }

  render() {
    const locations = this.props.currentCompanyStore?.locations || []
    const categories = this.props.currentCompanyStore?.productStore?.categories || []

    return (
      <div>
        {this.state.affectedEntries !== undefined ? (
          <>
            <br />
            <div className='text-muted'>
              {this.state.affectedEntries} product{this.state.affectedEntries !== 1 && 's'} updated
            </div>
            <br />
          </>
        ) : (
          <>
            <Alert
              message={
                <>
                  <p>Bulk update low stock levels for products at each of your locations.</p>
                  <div>
                    Using this information, {BLAZE_COMPANY} can send you e-mail alerts when a
                    product runs low (see Inventory {'>'} Low stock alerts).
                  </div>
                </>
              }
            />
            <br />
            <div className='form-group'>
              <label className='text-muted'>Location (required)</label>
              <Select<number | undefined>
                className='w-100'
                placeholder='Location'
                allowClear
                dropdownMatchSelectWidth={false}
                value={this.state.locationId}
                onChange={(locationId) => this.setState({ locationId })}
              >
                {locations.map((location, index) => (
                  <Select.Option key={index} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className='form-group'>
              <label className='text-muted'>Category (optional)</label>
              <Select<string | undefined>
                className='w-100'
                placeholder='Category'
                allowClear
                dropdownMatchSelectWidth={false}
                value={this.state.categoryId}
                onChange={(categoryId) => this.setState({ categoryId })}
              >
                {categories.map((category, index) => (
                  <Select.Option key={index} value={category.id}>
                    {category.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className='form-group'>
              <label className='text-muted'>Product unit type (optional)</label>
              <Select<Unit | undefined>
                className='w-100'
                placeholder='Unit'
                allowClear
                dropdownMatchSelectWidth={false}
                value={this.state.unit}
                onChange={(unit) => this.setState({ unit })}
              >
                {this.unitOptions.map((unitOption, index) => (
                  <Select.Option key={index} value={unitOption.value}>
                    {unitOption.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className='form-group'>
              <label className='text-muted'>Low stock level</label>
              <InputNumber
                className='w-100'
                onChange={(value) => {
                  let lowStockQuantity: number | undefined =
                    typeof value === 'string' ? parseInt(value) : value || 0

                  if (isNaN(lowStockQuantity)) {
                    lowStockQuantity = undefined
                  }

                  this.setState({ lowStockQuantity })
                }}
              />

              {this.state.lowStockQuantity === undefined && (
                <div className='text-muted mt-1'>Existing low stock values will be cleared</div>
              )}
            </div>

            <div className='form-group'>
              <label className='text-muted'>Restock level</label>
              <InputNumber
                className='w-100'
                onChange={(value) => {
                  let restockQuantity: number | undefined =
                    typeof value === 'string' ? parseInt(value) : value || 0

                  if (isNaN(restockQuantity)) {
                    restockQuantity = undefined
                  }

                  this.setState({ restockQuantity })
                }}
              />
              {this.state.lowStockQuantity === undefined && (
                <div className='text-muted mt-1'>Existing restock values will be cleared</div>
              )}
            </div>
            <br />
            <div>{this.saveButton}</div>
          </>
        )}
      </div>
    )
  }
}
